import React, { useState } from 'react'
import { Navigate } from 'react-router-dom'
import { useGetAuthenticatedUser } from '../../../Flux/Selector/index.js'
import { isGlobalAdmin } from '../../../Model/User/IUser.js'
import { Environment } from '../../../environment.js'
import type { StatRow } from '../../../Repository/Admin/AdminStatsRepository.js'
import { METRICS, type ChartMetric, type KpiMetric, type Scope } from './metrics.js'
import { StatsBarChart } from './StatsBarChart.js'
import { StatsLineChart } from './StatsLineChart.js'
import { Card, KpiTile } from './StatsUi.js'
import { useStatsData } from './useStatsData.js'

const renderChart = (metric: ChartMetric, rows: StatRow[]) =>
  metric.kind === 'line' ? (
    <StatsLineChart data={metric.data(rows)} color={metric.color} zoom />
  ) : (
    <StatsBarChart data={metric.data(rows)} color={metric.color} rotateLabels={metric.rotateLabels} />
  )

const Section: React.FC<{ scope: Scope; title: string; rows: StatRow[] }> = ({ scope, title, rows }) => {
  const kpis = METRICS.filter((m) => m.scope === scope && m.kind === 'kpi') as KpiMetric[]
  const charts = METRICS.filter((m) => m.scope === scope && m.kind !== 'kpi') as ChartMetric[]
  return (
    <section className="space-y-4">
      <h2 className="text-xl font-semibold">
        {title} ({rows.length})
      </h2>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {kpis.map((m) => (
          <KpiTile key={m.id} label={m.title} value={m.value(rows)} to={`/admin/stats/m/${m.id}`} />
        ))}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {charts.map((m) => (
          <Card key={m.id} title={m.title} to={`/admin/stats/m/${m.id}`} summary={m.summary?.(rows)}>
            {renderChart(m, rows)}
          </Card>
        ))}
      </div>
    </section>
  )
}

export const AdminStatsPage = () => {
  const user = useGetAuthenticatedUser()
  const [excludeInternal, setExcludeInternal] = useState(true)
  const { orders, projects, loading, error } = useStatsData(excludeInternal)

  if (!user) return <></>
  if (!isGlobalAdmin(user)) return <Navigate to="/home" replace />

  return (
    <div className="space-y-8">
      <header className="flex flex-wrap items-center justify-between gap-4">
        <h1 className="text-3xl font-bold">Admin Stats</h1>
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" className="toggle toggle-sm" checked={excludeInternal} onChange={(e) => setExcludeInternal(e.target.checked)} />
            Exclude internal orgs
          </label>
          <a className="link link-primary text-sm" href={`${Environment.API_URL}/admin/stats/orders.csv?excludeInternal=${excludeInternal}`}>
            orders.csv
          </a>
          <a className="link link-primary text-sm" href={`${Environment.API_URL}/admin/stats/projects.csv?excludeInternal=${excludeInternal}`}>
            projects.csv
          </a>
        </div>
      </header>

      {loading && <p className="text-gray-500">Loading…</p>}
      {error && <p className="text-error">Could not load stats: {error}</p>}

      {!loading && !error && (
        <>
          <Section scope="orders" title="Orders" rows={orders} />
          <Section scope="projects" title="Projects" rows={projects} />
        </>
      )}
    </div>
  )
}
